/**
 * ÉPRIS Priority/Gift Toggle — Bulletproof IIFE Variante
 *
 * Refactor 2026-05-04 (Final):
 *
 * Root-Cause des "stale Toggle"-Bugs gefunden:
 * Das Theme nutzt `document.createRange().createContextualFragment(...)` für
 * Section-Replace (theme.js:4861). Anders als innerHTML führt
 * createContextualFragment EINGEBETTETE <script>-Tags AUS, wenn das Fragment
 * via replaceChildren in den DOM gehängt wird. Dieses Script ist via
 * `{{ 'gift-or-priority-toggle.js' | asset_url | script_tag }}` INSIDE der
 * cart-drawer-Section eingebunden → bei jedem Section-Replace lief der gesamte
 * Script-Body erneut → `const TOGGLE_VARIANTS` warf SyntaxError (already declared)
 * → Script-Execution abbrach → Listener wurden nicht mehr aktualisiert.
 *
 * Lösung: Entire Body im IIFE, plus Early-Return-Guard auf window-Flag. Erste
 * Ausführung initialisiert alles, alle Folge-Ausführungen returnen sofort.
 * Doc-level Delegation reicht — der eine Listener auf document überlebt
 * jeden Section-Replace und fängt change-Events von neuen Toggles via Bubble.
 */
(function () {
  'use strict';

  // Hard-Guard: Body läuft nur EINMAL pro Page-Load. Re-Inject via
  // createContextualFragment hat hier keinen Effekt mehr.
  if (window.__eprisPriorityToggleInit) return;
  window.__eprisPriorityToggleInit = true;

  window.PriorityConfig = {
    GIFT_ID: 49521187422551,
    PRIORITY_ID: 50166180086103,
    DRAWER_SECTION: 'cart-drawer',
    TOTAL_SELECTOR: '[data-hulkapps-cart-total]'
  };

  const TOGGLE_VARIANTS = {
    'priority-toggle': window.PriorityConfig.PRIORITY_ID,
    'gift-toggle': window.PriorityConfig.GIFT_ID,
  };

  // Inflight-Lock per toggle-id — verhindert Doppel-Fetch bei rapid-clicking
  const inflight = Object.create(null);

  window.PriorityToggle = { handleObjChange, updateUI };

  async function handleObjChange(e, toggle, variantId) {
    const toggleId = toggle.id;
    if (inflight[toggleId]) return;
    inflight[toggleId] = true;
    // Bug 2026-05-04: Theme portalisiert die Drawer in <body> ohne die Original-Section
    // zu cleanen → 2 cart-drawer-Elements in DOM, beide mit id="cart-drawer". Wir
    // merken uns den Drawer der den GEKLICKTEN Toggle enthält und arbeiten gegen den.
    const sourceDrawer = toggle.closest('cart-drawer');

    toggle.disabled = true;
    document.body.style.cursor = 'wait';
    const isAdding = e.target.checked;
    const config = window.PriorityConfig;

    try {
      let cartData;
      let sectionHtml;

      if (isAdding) {
        const [addRes, cartRes] = await Promise.all([
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: config.DRAWER_SECTION
            })
          }),
          fetch('/cart.js')
        ]);

        if (!addRes.ok) throw new Error('cart/add failed: ' + addRes.status);
        const addData = await addRes.json();
        cartData = await cartRes.json();
        sectionHtml = addData.sections && addData.sections[config.DRAWER_SECTION];
      } else {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: String(variantId),
            quantity: 0,
            sections: config.DRAWER_SECTION
          })
        });

        if (!res.ok) throw new Error('cart/change failed: ' + res.status);
        cartData = await res.json();
        sectionHtml = cartData.sections && cartData.sections[config.DRAWER_SECTION];
      }

      updateUI(sectionHtml, cartData, sourceDrawer);
    } catch (err) {
      console.error('[priority-toggle]', err);
      const live = document.getElementById(toggleId);
      if (live) live.checked = !isAdding;
    } finally {
      inflight[toggleId] = false;
      const live = document.getElementById(toggleId);
      if (live) live.disabled = false;
      document.body.style.cursor = 'default';
    }
  }

  function updateUI(html, cartData, preferredDrawer) {
    const config = window.PriorityConfig;

    if (html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('#cart-drawer');

      if (newDrawer) {
        // Update ALLE cart-drawer-Instanzen (theme portalisiert manchmal eine Kopie nach <body>,
        // wir wollen alle in sync halten). Bevorzugt den Drawer der den geklickten Toggle
        // enthält (= der sichtbare offene), fallt zurück auf erste querySelectorAll.
        const allDrawers = document.querySelectorAll('cart-drawer, #cart-drawer');
        if (allDrawers.length === 0) {
          // Kein Drawer im DOM — sollte nicht passieren, aber defensive
          console.warn('[priority-toggle] kein cart-drawer im DOM gefunden für updateUI');
        } else {
          allDrawers.forEach((d) => {
            // Skip self-targeting wenn der DOMParser-Doc-Drawer reinrutscht
            if (d.ownerDocument === document) {
              d.innerHTML = newDrawer.innerHTML;
            }
          });
        }
      }
    }

    const totalElem = document.querySelector(config.TOTAL_SELECTOR);
    if (totalElem && cartData && cartData.total_price != null) {
      totalElem.textContent = (cartData.total_price / 100).toFixed(2).replace('.', ',') + ' €';
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));

    if (typeof window.updateCartProgressBar === 'function' && cartData) {
      window.updateCartProgressBar(cartData);
    }
  }

  /* ============================================================
     Document-level Event-Delegation
     ============================================================
     Ein Listener auf document fängt change-Events von priority-toggle und
     gift-toggle ab. document wird nie ersetzt → Listener überlebt alle
     Cart-Drawer-Section-Replaces. Change-Events bubbeln vom <input> bis
     document, wir matchen per id. */
  function delegatedChangeHandler(e) {
    const target = e.target;
    if (!target || !target.id) return;
    const variantId = TOGGLE_VARIANTS[target.id];
    if (variantId == null) return;
    handleObjChange(e, target, variantId);
  }

  document.addEventListener('change', delegatedChangeHandler, false);
})();
